import React, { useEffect, useMemo, useState, useCallback } from "react";
import Modal from "../common/Modal";
import { useApp } from "../../contexts/AppContext";
import {
  SEGMENT_FIELDS,
  getOperatorsForField,
  getValueEditorType,
  DEFAULT_TIME_WINDOW,
  DAYS_OF_WEEK,
  getFieldOptionConfig,
  useFieldOptionConfig,
} from "../../constants/segmentOptions";

const uid = () =>
  crypto.randomUUID?.() ??
  `c-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const DEFAULT_SEGMENT = {
  name: "",
  description: "",
  definition: {
    all: [],
    any: [],
    none: [],
  },
  cpm: "",
  kind: 2, // 0 - static, 1 - dynamic, 2 - all
};

const ensureArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const parseMultiValue = (raw, valueType) => {
  const base = ensureArray(raw);
  if (valueType === "number") {
    return base.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
  }
  return base;
};

const toDisplayMultiValue = (value) => {
  if (!value) return "";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
};

const normalizeCondition = (condition) => {
  const { id: _id, value, ...rest } = condition;
  if (rest.op === "exists") {
    return rest;
  }

  if (rest.op === "between") {
    const [min, max] = Array.isArray(value) ? value : [];
    return { ...rest, value: [Number(min), Number(max)] };
  }

  if (rest.op === "timeWindow") {
    const schedule = value || DEFAULT_TIME_WINDOW;
    const rules = (schedule.rules || []).map((rule) => ({
      days: Array.isArray(rule.days) ? rule.days : ensureArray(rule.days),
      start: rule.start,
      end: rule.end,
    }));
    return { ...rest, value: { tz: schedule.tz || "UTC", rules } };
  }

  return { ...rest, value };
};

const definitionListToEditor = (list) =>
  (list || []).map((condition) => ({
    id: uid(),
    ...condition,
  }));

const mapSegmentToForm = (segment) => {
  if (!segment)
    return {
      ...DEFAULT_SEGMENT,
      definition: { ...DEFAULT_SEGMENT.definition },
    };

  return {
    ...DEFAULT_SEGMENT,
    ...segment,
    definition: {
      all: definitionListToEditor(segment.definition?.all),
      any: definitionListToEditor(segment.definition?.any),
      none: definitionListToEditor(segment.definition?.none),
    },
  };
};

const TimeWindowRule = ({ rule, onChange, onRemove }) => {
  const handleDayToggle = (day) => {
    const next = new Set(
      Array.isArray(rule.days) ? rule.days : ensureArray(rule.days),
    );
    if (next.has(day)) {
      next.delete(day);
    } else {
      next.add(day);
    }
    onChange({ ...rule, days: Array.from(next) });
  };

  return (
    <div className="builder-block">
      <div className="builder-block-header">
        <span>Rule</span>
        <button
          type="button"
          className="btn-table-action danger"
          onClick={onRemove}
        >
          Remove
        </button>
      </div>
      <div className="builder-grid">
        <div className="form-group-modal">
          <label>Days</label>
          <div className="day-selector">
            {DAYS_OF_WEEK.map((day) => {
              const isActive = Array.isArray(rule.days)
                ? rule.days.includes(day.value)
                : ensureArray(rule.days).includes(day.value);
              return (
                <button
                  key={day.value}
                  type="button"
                  className={`chip ${isActive ? "chip-active" : ""}`}
                  onClick={() => handleDayToggle(day.value)}
                >
                  {day.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="form-group-modal">
          <label htmlFor={`start-${rule.id}`}>Start</label>
          <input
            id={`start-${rule.id}`}
            type="time"
            value={rule.start || "00:00"}
            onChange={(e) => onChange({ ...rule, start: e.target.value })}
          />
        </div>
        <div className="form-group-modal">
          <label htmlFor={`end-${rule.id}`}>End</label>
          <input
            id={`end-${rule.id}`}
            type="time"
            value={rule.end || "23:59"}
            onChange={(e) => onChange({ ...rule, end: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
};

const TimeWindowEditor = ({ value, onChange }) => {
  const schedule = value || DEFAULT_TIME_WINDOW;
  const rules = (schedule.rules || []).map((rule) => ({
    id: rule.id ?? uid(),
    days: Array.isArray(rule.days) ? rule.days : ensureArray(rule.days),
    start: rule.start || "00:00",
    end: rule.end || "23:59",
  }));

  const handleRuleChange = (ruleId, nextRule) => {
    const updated = rules.map((r) => (r.id === ruleId ? nextRule : r));
    onChange({ ...schedule, rules: updated });
  };

  const handleAddRule = () => {
    const next = [
      ...rules,
      {
        id: uid(),
        days: ["mon"],
        start: "09:00",
        end: "18:00",
      },
    ];
    onChange({ ...schedule, rules: next });
  };

  const handleRemoveRule = (ruleId) => {
    const next = rules.filter((r) => r.id !== ruleId);
    onChange({ ...schedule, rules: next });
  };

  return (
    <div className="builder-block">
      <div className="form-group-modal">
        <label htmlFor="timezone">Timezone</label>
        <input
          id="timezone"
          type="text"
          value={schedule.tz || "UTC"}
          onChange={(e) => onChange({ ...schedule, tz: e.target.value })}
          placeholder="e.g. Europe/Kyiv"
        />
      </div>
      {rules.map((rule) => (
        <TimeWindowRule
          key={rule.id}
          rule={rule}
          onChange={(nextRule) => handleRuleChange(rule.id, nextRule)}
          onRemove={() => handleRemoveRule(rule.id)}
        />
      ))}
      <button
        type="button"
        className="btn btn-secondary"
        onClick={handleAddRule}
      >
        + Add Time Window
      </button>
    </div>
  );
};

const createDefaultCondition = (
  fieldValue = "country",
  fieldOptionConfig = null,
) => {
  const fieldDef =
    SEGMENT_FIELDS.find((f) => f.value === fieldValue) || SEGMENT_FIELDS[1];
  const operators = getOperatorsForField(fieldDef.value);
  const defaultOperator = operators[0] || {
    value: "eq",
    types: [fieldDef.type],
  };
  const optionConfig = fieldOptionConfig
    ? getFieldOptionConfig(fieldDef.value, fieldOptionConfig)
    : getFieldOptionConfig(fieldDef.value);
  const editorType = getValueEditorType(defaultOperator);
  let value;

  if (optionConfig) {
    if (editorType === "multi" || optionConfig.type === "multi") {
      value = [];
    } else {
      const firstOption = optionConfig.options[0];
      value = firstOption
        ? optionConfig.toRaw(firstOption.value)
        : fieldDef.type === "number"
          ? 0
          : "";
    }
  } else {
    switch (editorType) {
      case "range":
        value = [0, 0];
        break;
      case "timeWindow":
        value = DEFAULT_TIME_WINDOW;
        break;
      case "none":
        value = undefined;
        break;
      case "multi":
        value = [];
        break;
      default:
        value = fieldDef.type === "number" ? 0 : "";
    }
  }

  return {
    id: uid(),
    field: fieldDef.value,
    op: defaultOperator.value,
    value,
    ci: fieldDef.type === "string" ? true : undefined,
    disabled: false,
  };
};

const ConditionEditor = ({
  condition,
  onChange,
  onRemove,
  fieldOptionConfig = null,
}) => {
  const field =
    SEGMENT_FIELDS.find((f) => f.value === condition.field) ||
    SEGMENT_FIELDS[1];
  const operators = getOperatorsForField(field?.value);
  const operator =
    operators.find((op) => op.value === condition.op) || operators[0];
  const valueEditorType = getValueEditorType(operator);
  const optionConfig = fieldOptionConfig
    ? getFieldOptionConfig(field?.value, fieldOptionConfig)
    : getFieldOptionConfig(field?.value);

  const stringifyOption =
    optionConfig?.toString ??
    ((value) => (value === undefined || value === null ? "" : String(value)));
  const parseOption =
    optionConfig?.toRaw ??
    ((value) => (field.type === "number" ? Number(value) : value));

  const handleFieldChange = (value) => {
    const nextField = SEGMENT_FIELDS.find((f) => f.value === value) || field;
    const nextOperators = getOperatorsForField(nextField.value);
    const fallbackOperator = nextOperators[0]?.value ?? "eq";
    const nextOptionConfig = fieldOptionConfig
      ? getFieldOptionConfig(nextField.value, fieldOptionConfig)
      : getFieldOptionConfig(nextField.value);
    const nextOperator =
      nextOperators.find((op) => op.value === fallbackOperator) ||
      nextOperators[0];
    const nextValueEditorType = getValueEditorType(nextOperator);
    let nextValue;

    if (nextOptionConfig) {
      if (
        nextValueEditorType === "multi" ||
        nextOptionConfig.type === "multi"
      ) {
        nextValue = [];
      } else {
        const firstOption = nextOptionConfig.options[0];
        nextValue = firstOption
          ? nextOptionConfig.toRaw(firstOption.value)
          : nextField.type === "number"
            ? 0
            : "";
      }
    } else {
      if (nextValueEditorType === "multi") {
        nextValue = [];
      } else if (nextValueEditorType === "range") {
        nextValue = [0, 0];
      } else if (nextValueEditorType === "timeWindow") {
        nextValue = DEFAULT_TIME_WINDOW;
      } else if (nextValueEditorType === "none") {
        nextValue = undefined;
      } else {
        nextValue = nextField.type === "number" ? 0 : "";
      }
    }

    onChange({
      ...condition,
      field: nextField.value,
      op: fallbackOperator,
      value: nextValue,
      ci: nextField.type === "string" ? (condition.ci ?? true) : undefined,
    });
  };

  const handleOperatorChange = (value) => {
    const nextOperator = operators.find((op) => op.value === value) || operator;
    let nextValue = condition.value;

    switch (getValueEditorType(nextOperator)) {
      case "none":
        nextValue = undefined;
        break;
      case "range":
        nextValue = [0, 0];
        break;
      case "multi": {
        if (optionConfig) {
          nextValue = Array.isArray(condition.value) ? condition.value : [];
        } else {
          nextValue = [];
        }
        break;
      }
      case "timeWindow":
        nextValue = DEFAULT_TIME_WINDOW;
        break;
      default:
        if (optionConfig) {
          const firstOption = optionConfig.options[0];
          nextValue = firstOption
            ? optionConfig.toRaw(firstOption.value)
            : nextOperator.types.includes("number")
              ? 0
              : "";
        } else {
          nextValue = nextOperator.types.includes("number") ? 0 : "";
        }
    }

    onChange({
      ...condition,
      op: nextOperator.value,
      value: nextValue,
      ci: field.type === "string" ? (condition.ci ?? true) : undefined,
    });
  };

  const handleValueChange = (value) => {
    onChange({ ...condition, value });
  };

  const renderValueEditor = () => {
    if (valueEditorType === "none") {
      return null;
    }

    if (valueEditorType === "range") {
      const [min, max] = Array.isArray(condition.value)
        ? condition.value
        : [0, 0];
      return (
        <div className="range-inputs">
          <input
            type="number"
            value={min ?? 0}
            onChange={(e) =>
              handleValueChange([
                Number(e.target.value),
                Array.isArray(condition.value) ? condition.value[1] : 0,
              ])
            }
          />
          <span className="range-separator">to</span>
          <input
            type="number"
            value={max ?? 0}
            onChange={(e) =>
              handleValueChange([
                Array.isArray(condition.value) ? condition.value[0] : 0,
                Number(e.target.value),
              ])
            }
          />
        </div>
      );
    }

    if (valueEditorType === "multi") {
      if (optionConfig) {
        const currentValues = Array.isArray(condition.value)
          ? condition.value
          : [];
        const toggleOption = (optionValue) => {
          const stringValue = stringifyOption(optionValue);
          const exists = currentValues.some(
            (item) => stringifyOption(item) === stringValue,
          );
          const next = exists
            ? currentValues.filter(
              (item) => stringifyOption(item) !== stringValue,
            )
            : [...currentValues, optionValue];
          handleValueChange(next);
        };

        const getLabelForValue = (optionValue) => {
          const stringValue = stringifyOption(optionValue);
          const match = optionConfig.options.find(
            (opt) => stringifyOption(parseOption(opt.value)) === stringValue,
          );
          return match?.label ?? stringValue;
        };

        return (
          <>
            <div className="segment-picker">
              {optionConfig.options.map((opt) => {
                const optionValue = parseOption(opt.value);
                const stringValue = stringifyOption(optionValue);
                const isChecked = currentValues.some(
                  (item) => stringifyOption(item) === stringValue,
                );
                return (
                  <label
                    key={opt.value}
                    className={`segment-picker-option ${isChecked ? "selected" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleOption(optionValue)}
                    />
                    <span className="segment-picker-label">
                      {opt.label}
                      {opt.kind && (
                        <span className={`kind-tag kind-tag-${opt.kind}`}>
                          {opt.kind}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
            {currentValues.length > 0 && (
              <div className="segment-picker-summary">
                {currentValues.map((value) => {
                  const stringValue = stringifyOption(value);
                  const label = getLabelForValue(value);
                  return (
                    <span key={stringValue} className="segment-chip">
                      {label}
                      <button
                        type="button"
                        onClick={() => toggleOption(value)}
                        aria-label="Remove value"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </>
        );
      }

      const isNumeric = field.type === "number";
      return (
        <input
          type="text"
          value={toDisplayMultiValue(condition.value)}
          onChange={(e) =>
            handleValueChange(
              parseMultiValue(e.target.value, isNumeric ? "number" : "string"),
            )
          }
          placeholder={
            isNumeric ? "Comma separated numbers" : "Comma separated values"
          }
        />
      );
    }

    if (valueEditorType === "timeWindow") {
      return (
        <TimeWindowEditor
          value={condition.value}
          onChange={handleValueChange}
        />
      );
    }

    if (optionConfig) {
      const currentValue = stringifyOption(condition.value);
      return (
        <select
          value={currentValue}
          onChange={(event) =>
            handleValueChange(optionConfig.toRaw(event.target.value))
          }
        >
          {optionConfig.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
              {opt.kind ? ` [${opt.kind}]` : ""}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === "boolean") {
      const current =
        condition.value === true
          ? "true"
          : condition.value === false
            ? "false"
            : "";
      return (
        <select
          value={current}
          onChange={(e) => handleValueChange(e.target.value === "true")}
        >
          <option value="">Select value</option>
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      );
    }

    const inputType = field.type === "number" ? "number" : "text";

    return (
      <input
        type={inputType}
        value={condition.value ?? ""}
        onChange={(e) => {
          const raw = e.target.value;
          if (field.type === "number") {
            handleValueChange(raw === "" ? null : Number(raw));
          } else {
            handleValueChange(raw);
          }
        }}
        placeholder={field.type === "number" ? "Enter number" : "Enter value"}
        inputMode={field.type === "number" ? "decimal" : undefined}
      />
    );
  };

  return (
    <div className="condition-card">
      <div className="condition-header">
        <h4>Condition</h4>
        <button
          type="button"
          className="btn-table-action danger"
          onClick={onRemove}
        >
          Remove
        </button>
      </div>
      <div className="condition-grid">
        <div className="form-group-modal">
          <label htmlFor={`field-${condition.id}`}>Field</label>
          <select
            id={`field-${condition.id}`}
            value={condition.field}
            onChange={(e) => handleFieldChange(e.target.value)}
          >
            {SEGMENT_FIELDS.filter((f) => !f.deprecated).map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group-modal">
          <label htmlFor={`operator-${condition.id}`}>Operator</label>
          <select
            id={`operator-${condition.id}`}
            value={condition.op}
            onChange={(e) => handleOperatorChange(e.target.value)}
          >
            {operators.map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group-modal">
          <label>Value</label>
          {renderValueEditor()}
        </div>
      </div>
      {field.type === "string" && valueEditorType !== "timeWindow" && (
        <div className="condition-flags">
          <label>
            <input
              type="checkbox"
              checked={condition.ci ?? true}
              onChange={(e) => onChange({ ...condition, ci: e.target.checked })}
            />
            Case insensitive
          </label>
        </div>
      )}
      <div className="condition-flags">
        <label>
          <input
            type="checkbox"
            checked={condition.disabled ?? false}
            onChange={(e) =>
              onChange({ ...condition, disabled: e.target.checked })
            }
          />
          Disable condition
        </label>
      </div>
    </div>
  );
};

const ConditionGroup = ({
  title,
  description,
  conditions,
  onAdd,
  onUpdate,
  onRemove,
  fieldOptionConfig = null,
}) => (
  <section className="condition-group">
    <header className="condition-group-header">
      <div>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
    </header>
    {conditions.length === 0 ? (
      <div className="empty-state small">
        <p>No conditions yet</p>
      </div>
    ) : (
      conditions.map((condition) => (
        <ConditionEditor
          key={condition.id}
          condition={condition}
          onChange={(next) => onUpdate(condition.id, next)}
          onRemove={() => onRemove(condition.id)}
          fieldOptionConfig={fieldOptionConfig}
        />
      ))
    )}
    <div className="condition-actions">
      <button type="button" className="btn btn-secondary" onClick={onAdd}>
        + Add condition
      </button>
    </div>
  </section>
);

const VAST_COUNT_DEBOUNCE_MS = 5000;

export default function BuildSegmentModal({
  isOpen,
  onClose,
  initialSegment,
  onSave,
  onGenerateDefinition,
}) {
  const { previewSegmentCount } = useApp();
  const fieldOptionConfig = useFieldOptionConfig();
  const [form, setForm] = useState(() => mapSegmentToForm(initialSegment));
  const [isSaving, setIsSaving] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiSummary, setAiSummary] = useState("");
  const [aiError, setAiError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [vastCount, setVastCount] = useState(null);
  const [vastCountLoading, setVastCountLoading] = useState(false);
  const previewAbortRef = React.useRef(null);
  const previewTimerRef = React.useRef(null);
  const isFirstPreviewRef = React.useRef(true);
  const skipPreviewWhileSavingRef = React.useRef(false);

  const isEditMode = Boolean(initialSegment?.uuid);

  const calculateVastCount = useCallback(async () => {
    if (skipPreviewWhileSavingRef.current) return;

    const definition = {
      all: form.definition.all.map(normalizeCondition),
      any: form.definition.any.map(normalizeCondition),
      none: form.definition.none.map(normalizeCondition),
    };

    if (previewAbortRef.current) {
      previewAbortRef.current.abort();
    }

    const controller = new AbortController();
    previewAbortRef.current = controller;

    setVastCountLoading(true);

    try {
      const count = await previewSegmentCount(definition, form.kind, {
        signal: controller.signal,
      });

      setVastCount(count);
    } catch (error) {
      if (error?.name === 'AbortError') return;

      console.error('Failed to calculate Unique Impressions count', error);
      setVastCount(null);
    } finally {
      if (previewAbortRef.current === controller) {
        previewAbortRef.current = null;
        setVastCountLoading(false);
      }
    }
  }, [form.definition, form.kind, previewSegmentCount]);

  const applyGeneratedDefinition = (definition = {}) => {
    setForm((prev) => ({
      ...prev,
      definition: {
        all: definitionListToEditor(definition.all),
        any: definitionListToEditor(definition.any),
        none: definitionListToEditor(definition.none),
      },
    }));
  };

  const handleGenerateDefinition = async () => {
    if (!onGenerateDefinition) {
      setAiError("AI assistant is not available in this environment.");
      return;
    }

    const trimmedPrompt = aiPrompt.trim();
    if (!trimmedPrompt) {
      setAiError("Describe your target audience before sending.");
      return;
    }

    setIsGenerating(true);
    setAiError(null);

    try {
      const result = await onGenerateDefinition(trimmedPrompt);
      applyGeneratedDefinition(result.data || {});
      setAiSummary("Filters were updated based on your description.");
    } catch (error) {
      console.error("Failed to generate segment definition", error);
      setAiError("Unable to generate filters. Try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateDefinition = (group, updater) => {
    setForm((prev) => ({
      ...prev,
      definition: {
        ...prev.definition,
        [group]: updater(prev.definition[group] || []),
      },
    }));
  };

  const addCondition = (group) => {
    updateDefinition(group, (list) => [
      ...(list || []),
      createDefaultCondition(undefined, fieldOptionConfig),
    ]);
  };

  const updateCondition = (group, id, nextCondition) => {
    updateDefinition(group, (list) =>
      list.map((item) => (item.id === id ? nextCondition : item)),
    );
  };

  const removeCondition = (group, id) => {
    updateDefinition(group, (list) => list.filter((item) => item.id !== id));
  };

  const handleNumberChange = (field, value) => {
    if (value === "") {
      updateField(field, "");
      return;
    }
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return;

    updateField(field, numericValue);
  };

  const cancelPreviewCount = useCallback(() => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }

    if (previewAbortRef.current) {
      previewAbortRef.current.abort();
      previewAbortRef.current = null;
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    cancelPreviewCount();
    skipPreviewWhileSavingRef.current = true;

    setIsSaving(true);
    try {
      const definition = {
        all: form.definition.all.map(normalizeCondition),
        any: form.definition.any.map(normalizeCondition),
        none: form.definition.none.map(normalizeCondition),
      };

      await onSave({ ...form, definition });
    } finally {
      setIsSaving(false);
      skipPreviewWhileSavingRef.current = false;
    }
  }, [cancelPreviewCount, form, onSave]);

  const modalFooter = useMemo(
    () => (
      <>
        <button
          className="btn btn-secondary"
          type="button"
          onClick={onClose}
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          type="button"
          disabled={isSaving}
          onClick={async () => {
            try {
              await handleSubmit();
            } catch (error) {
              console.error("Failed to submit segment", error);
            }
          }}
        >
          {isSaving
            ? isEditMode
              ? "Updating…"
              : "Creating…"
            : isEditMode
              ? "Update Segment"
              : "Create Segment"}
        </button>
      </>
    ),
    [handleSubmit, isEditMode, isSaving, onClose],
  );

  useEffect(() => {
    if (isOpen) {
      isFirstPreviewRef.current = true;
      cancelPreviewCount();

      setForm(mapSegmentToForm(initialSegment));
      setAiPrompt("");
      setAiSummary("");
      setAiError(null);
      setIsGenerating(false);
      setVastCount(null);
    } else {
      cancelPreviewCount();
    }
  }, [initialSegment, isOpen, cancelPreviewCount]);

  useEffect(() => {
    if (!isOpen) return;
    cancelPreviewCount();

    const run = () => {
      calculateVastCount();
    };

    if (isFirstPreviewRef.current) {
      isFirstPreviewRef.current = false;
      run();
      return;
    }

    previewTimerRef.current = setTimeout(run, VAST_COUNT_DEBOUNCE_MS);

    return () => {
      cancelPreviewCount();
    };
  }, [isOpen, form.definition, form.kind, calculateVastCount, cancelPreviewCount]);

  useEffect(() => {
    if (!isOpen) return;

    if (previewAbortRef.current) {
      previewAbortRef.current.abort();
      previewAbortRef.current = null;
    }

    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }

    if (isFirstPreviewRef.current) {
      isFirstPreviewRef.current = false;
      calculateVastCount();
      return;
    }

    previewTimerRef.current = setTimeout(() => {
      calculateVastCount();
    }, VAST_COUNT_DEBOUNCE_MS);

    return () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
        previewTimerRef.current = null;
      }
    };
  }, [isOpen, form.definition, form.kind, calculateVastCount]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEditMode ? "Edit Audience Segment" : "Build Audience Segment"}
        footer={modalFooter}
        size="xlarge"
      >
        <form
          className="builder-form"
          onSubmit={(event) => event.preventDefault()}
        >
          <div className="form-group-modal">
            <label htmlFor="segment-name">Segment name</label>
            <input
              id="segment-name"
              value={form.name}
              onChange={(e) => updateField("name", e.target.value)}
              placeholder="e.g. Wellness Enthusiasts"
              required
            />
          </div>
          <div className="form-group-modal">
            <label htmlFor="segment-description">Description</label>
            <textarea
              id="segment-description"
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder="Short summary of the segment..."
              rows={3}
            />
          </div>
          <div className="builder-subsection">
            <div className="form-group-modal">
              <label htmlFor="segment-cpm">CPM</label>
              <input
                id="segment-cpm"
                type="number"
                min="0"
                value={form.cpm ?? ""}
                onChange={(event) =>
                  handleNumberChange("cpm", event.target.value)
                }
                placeholder="e.g. 100"
              />
            </div>
            <div className="form-group-modal">
              <label htmlFor="segment-kind">Type</label>
              <select
                id="segment-kind"
                value={form.kind ?? 2}
                onChange={(e) =>
                  updateField("kind", parseInt(e.target.value, 10))
                }
              >
                <option value={0}>Static</option>
                <option value={1}>Dynamic</option>
                <option value={2}>All</option>
              </select>
            </div>
          </div>
          <div className="builder-divider" aria-hidden />
          <div className="builder-block ai-assistant-block">
            <div className="builder-block-header">
              <div>
                <h3>AI Assistant for Segment Building</h3>
                <div className="ai-assistant-message">
                  <span className="ai-assistant-avatar" aria-hidden="true">
                    🤖
                  </span>
                  <div className="ai-assistant-copy">
                    <strong>AI Assistant</strong>
                    <p>
                      Hello! I'll help you build an audience segment. Describe
                      the audience you want to target, and I'll help define the
                      segmentation criteria.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="ai-textarea-wrapper">
              <textarea
                className="ai-textarea"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Describe your target audience..."
                rows={3}
              />
              <button
                type="button"
                className="btn btn-primary ai-send-btn"
                onClick={handleGenerateDefinition}
                disabled={isGenerating || !aiPrompt.trim()}
              >
                {isGenerating ? "Generating…" : "Send"}
              </button>
            </div>
            {isGenerating && <p className="helper-text">Drafting filters…</p>}
            {aiError && <p className="helper-text error">{aiError}</p>}
            {aiSummary && !aiError && (
              <div className="ai-response">
                <strong>Suggested filters applied</strong>
                <p>{aiSummary}</p>
              </div>
            )}
          </div>
          <div className="builder-divider" aria-hidden />
          {/* <div className="form-group-modal">
          <label>Created by</label>
          <input
            type="text"
            value={initialSegment?.createdBy ?? 'You'}
            disabled
          />
        </div> */}
          <ConditionGroup
            title="Must match all of these"
            description="Every condition has to pass for a viewer to qualify."
            conditions={form.definition.all}
            onAdd={() => addCondition("all")}
            onUpdate={(id, next) => updateCondition("all", id, next)}
            onRemove={(id) => removeCondition("all", id)}
            fieldOptionConfig={fieldOptionConfig}
          />

          <ConditionGroup
            title="Match any of these"
            description="At least one condition must pass."
            conditions={form.definition.any}
            onAdd={() => addCondition("any")}
            onUpdate={(id, next) => updateCondition("any", id, next)}
            onRemove={(id) => removeCondition("any", id)}
            fieldOptionConfig={fieldOptionConfig}
          />

          <ConditionGroup
            title="Exclude viewers when"
            description="If any condition passes, the viewer is excluded."
            conditions={form.definition.none}
            onAdd={() => addCondition("none")}
            onUpdate={(id, next) => updateCondition("none", id, next)}
            onRemove={(id) => removeCondition("none", id)}
            fieldOptionConfig={fieldOptionConfig}
          />
        </form>
      </Modal>
      {isOpen && (
        <div className="vast-count-card">
          <div className="vast-count-card-header">
            <span className="vast-count-card-title">
              Estimated Unique Devices
            </span>
            <span className="vast-count-card-period">(Last month)</span>
          </div>
          <div className="vast-count-card-content">
            {vastCountLoading ? (
              <div className="vast-count-loader">
                <div className="spinner"></div>
                <span>Calculating...</span>
              </div>
            ) : vastCount !== null ? (
              <div className="vast-count-value">
                <span className="vast-count-number">
                  {vastCount.toLocaleString()}
                </span>
                <span className="vast-count-label">VASTs</span>
              </div>
            ) : (
              <div className="vast-count-placeholder">
                <span>Add conditions to see count</span>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
